import * as React from 'react';
import { useParams } from 'react-router-dom';
import { Button, Field, Label, Link, makeStyles, Select, Slider, Spinner, Text, tokens } from '@fluentui/react-components';
import { ArrowDownload20Regular, Open20Regular } from '@fluentui/react-icons';
import QRCode from 'qrcode';
import { useDataService, useAsync } from '../app/DataServiceContext';
import { useAppToast } from '../app/ToastContext';
import PageHeader from '../components/ui/PageHeader';
import Section from '../components/ui/Section';
import ErrorBar from '../components/ui/ErrorBar';
import CopyButton from '../components/ui/CopyButton';
import ColorField from '../components/ui/ColorField';
import { BlockSkeleton } from '../components/ui/Skeletons';
import { sanitizeFileName } from '../lib/format';

type ErrorLevel = 'L' | 'M' | 'Q' | 'H';

const useStyles = makeStyles({
    layout: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(280px, 360px)',
        gap: '20px',
        alignItems: 'start',
        '@media (max-width: 1023px)': {
            gridTemplateColumns: 'minmax(0, 1fr)',
        },
    },
    preview: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '20px',
        padding: '32px 24px',
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusLarge,
        minHeight: '360px',
        justifyContent: 'center',
    },
    image: {
        maxWidth: '100%',
        height: 'auto',
        borderRadius: tokens.borderRadiusMedium,
        boxShadow: tokens.shadow16,
        backgroundColor: '#fff',
    },
    url: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        overflowWrap: 'anywhere',
        textAlign: 'center',
        maxWidth: '460px',
    },
    actions: {
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap',
        justifyContent: 'center',
    },
    controls: {
        display: 'flex',
        flexDirection: 'column',
        gap: '18px',
    },
    sliderRow: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    sliderHead: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
    },
    hint: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
});

const PollQrPage: React.FC = () => {
    const styles = useStyles();
    const toast = useAppToast();
    const dataService = useDataService();
    const { id = '' } = useParams<{ id: string }>();
    const poll = useAsync(() => dataService.getPoll(id), [id]);

    const [foreground, setForeground] = React.useState('#000000');
    const [background, setBackground] = React.useState('#ffffff');
    const [size, setSize] = React.useState(280);
    const [margin, setMargin] = React.useState(1);
    const [level, setLevel] = React.useState<ErrorLevel>('M');
    const [dataUrl, setDataUrl] = React.useState('');
    const [generating, setGenerating] = React.useState(false);
    const [error, setError] = React.useState<string>();
    const seeded = React.useRef(false);

    const url = dataService.getSurveyUrl(poll.data?.Slug);

    // Take the saved brand colours as the starting point, once.
    React.useEffect(() => {
        if (!poll.data || seeded.current) return;
        seeded.current = true;
        if (poll.data.QrcodeForeground) setForeground(poll.data.QrcodeForeground);
        if (poll.data.QrcodeBackground) setBackground(poll.data.QrcodeBackground);
    }, [poll.data]);

    React.useEffect(() => {
        if (!url) return;
        let active = true;
        setGenerating(true);
        QRCode.toDataURL(url, { width: size, margin, errorCorrectionLevel: level, color: { dark: foreground, light: background } })
            .then((result) => { if (active) { setDataUrl(result); setError(undefined); } })
            .catch(() => { if (active) setError('Не удалось сгенерировать QR-код.'); })
            .finally(() => { if (active) setGenerating(false); });
        return () => { active = false; };
    }, [url, size, margin, level, foreground, background]);

    const baseName = `qr-${sanitizeFileName(poll.data?.Slug ?? poll.data?.Title ?? 'poll', 'poll')}`;

    const downloadPng = async (): Promise<void> => {
        if (!url) return;
        try {
            const png = await QRCode.toDataURL(url, { width: 1024, margin, errorCorrectionLevel: level, color: { dark: foreground, light: background } });
            const a = document.createElement('a');
            a.href = png;
            a.download = `${baseName}.png`;
            a.click();
        } catch {
            toast.error('Не удалось сохранить PNG');
        }
    };

    const downloadSvg = async (): Promise<void> => {
        if (!url) return;
        try {
            const svg = await QRCode.toString(url, { type: 'svg', margin, errorCorrectionLevel: level, color: { dark: foreground, light: background } });
            const blob = new Blob([svg], { type: 'image/svg+xml' });
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = `${baseName}.svg`;
            a.click();
            window.setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
        } catch {
            toast.error('Не удалось сохранить SVG');
        }
    };

    return (
        <>
            <PageHeader
                backTo={{ to: `/polls/${encodeURIComponent(id)}`, label: 'К опросу' }}
                title="QR-код"
                subtitle={poll.data?.Title}
            />

            <ErrorBar message={poll.error} title="Не удалось загрузить опрос" onRetry={poll.reload} />

            {poll.loading && !poll.data && <Section><BlockSkeleton /></Section>}

            {poll.data && (
                <div className={styles.layout}>
                    <Section title="Предпросмотр" description="PNG для скачивания всегда 1024 px, SVG масштабируется без потерь.">
                        <div className={styles.preview}>
                            {!url && <Text className={styles.hint}>У опроса ещё нет ссылки: slug не назначен.</Text>}
                            {url && generating && !dataUrl && <Spinner label="Генерация" />}
                            {url && dataUrl && (
                                <img src={dataUrl} alt={`QR-код ссылки ${url}`} width={size} height={size} className={styles.image} />
                            )}
                            {error && <Text style={{ color: tokens.colorStatusDangerForeground1 }}>{error}</Text>}
                            {url && (
                                <Link href={url} target="_blank" rel="noopener noreferrer" className={styles.url}>{url}</Link>
                            )}
                            <div className={styles.actions}>
                                <Button appearance="primary" icon={<ArrowDownload20Regular />} disabled={!dataUrl} onClick={() => { void downloadPng(); }}>PNG</Button>
                                <Button appearance="secondary" icon={<ArrowDownload20Regular />} disabled={!url} onClick={() => { void downloadSvg(); }}>SVG</Button>
                                <CopyButton value={url} label="Копировать ссылку" successMessage="Ссылка скопирована" text="Ссылка" size="medium" appearance="secondary" />
                                <Button appearance="secondary" icon={<Open20Regular />} disabled={!url} onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}>Открыть</Button>
                            </div>
                        </div>
                    </Section>

                    <Section title="Параметры">
                        <div className={styles.controls}>
                            <ColorField label="Цвет модулей" value={foreground} onChange={setForeground} />
                            <ColorField label="Цвет фона" value={background} onChange={setBackground} />

                            <div className={styles.sliderRow}>
                                <div className={styles.sliderHead}>
                                    <Label htmlFor="qr-size">Размер предпросмотра</Label>
                                    <Text className={styles.hint}>{size} px</Text>
                                </div>
                                <Slider id="qr-size" min={120} max={480} step={20} value={size} onChange={(_, data) => setSize(data.value)} />
                            </div>

                            <div className={styles.sliderRow}>
                                <div className={styles.sliderHead}>
                                    <Label htmlFor="qr-margin">Отступ по краям</Label>
                                    <Text className={styles.hint}>{margin} мод.</Text>
                                </div>
                                <Slider id="qr-margin" min={0} max={4} step={1} value={margin} onChange={(_, data) => setMargin(data.value)} />
                            </div>

                            <Field label="Коррекция ошибок" hint="Выше уровень: код читается даже при частичном повреждении, но плотнее.">
                                <Select value={level} onChange={(_, data) => setLevel(data.value as ErrorLevel)}>
                                    <option value="L">L, низкая (~7%)</option>
                                    <option value="M">M, средняя (~15%)</option>
                                    <option value="Q">Q, высокая (~25%)</option>
                                    <option value="H">H, максимальная (~30%)</option>
                                </Select>
                            </Field>
                        </div>
                    </Section>
                </div>
            )}
        </>
    );
};

export default PollQrPage;
